import React, { useCallback, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { Form, Formik } from 'formik';

import dayjs from 'dayjs';

import { useUploadFreelancerCertificateDocument } from 'state/remote/remote-freelancer/use-upload-freelancer-certification-document';
import useGetCertificateTypes from 'state/remote/remote-freelancer/use-get-certificate-types';

import type { CertificateUploadType } from 'state/remote/remote-freelancer/use-upload-freelancer-certification-document';

import { createLocale } from 'locales';

import Button from 'components-new/button/button';
import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';
import Typography from 'components-new/typography/typography';

import ComboBoxField from 'form-elements/combo-box-field';
import DateInputField from 'form-elements/date-input-field';
import FileInputField from 'form-elements/file-input-field';

import {
  SUPPORTED_DOC_TYPES,
  STATIC_FILES_URL,
} from 'constants/signup-flow-constants';

import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';

import freelancerRoutes from '../freelancer-routes';

import CertificatesInformationDialog from '../components/certificates-information-dialog';

import validationSchema from './schema';

const globalLocalize = createLocale('global');
const localize = createLocale('freelancerAddCertificationDocumentPageLocales');

const AddCertificationDocument: React.FC = () => {
  const {
    data,
    certificatesTypes,
    isLoading: certTypesLoading,
  } = useGetCertificateTypes();

  const videoUrl = `${STATIC_FILES_URL}videos/signupflow/h8.mp4`;
  const videoPreviewImageURL = `${window.STATIC_URL}assets/signupflow/h8.png`;

  const {
    initialValues,
    isLoading,
    uploadFreelancerCertificateDocument,
    isSuccess,
    resetUploadStatus,
    error,
  } = useUploadFreelancerCertificateDocument();
  const navigate = useNavigate();

  const goToCertificatesInformation = useCallback(
    (certificateAdded?: boolean) => {
      if (certificateAdded)
        navigate(freelancerRoutes.certificationInformation.path, {
          state: { certificateAdded: true },
        });
      else navigate(freelancerRoutes.certificationInformation.path);
    },
    [navigate],
  );

  const handleSubmitCertificate = useCallback(
    (values: typeof initialValues) => {
      const docToUpload: CertificateUploadType = {
        dtype: Number(values.dtype),
      };
      if (dayjs(values.validUntil).isValid())
        docToUpload.valid_until = values.validUntil;
      if (values.file.type === 'application/pdf') docToUpload.pdf = values.file;
      else docToUpload.picture = values.file;
      uploadFreelancerCertificateDocument(docToUpload);
    },
    [uploadFreelancerCertificateDocument],
  );

  useEffect(() => {
    if (isSuccess) {
      showSuccessToast({
        description: localize('certificateAdded'),
        title: globalLocalize('notification'),
      });
      resetUploadStatus();
      goToCertificatesInformation();
    }
    if (error) {
      showErrorToast({
        description: error.message,
        title: globalLocalize('error'),
      });
      resetUploadStatus();
    }
    // eslint-disable-next-line
  }, [error, isSuccess]);

  if (!data.length && !certTypesLoading)
    return (
      <Typography
        as="h4"
        variant="body-large-bold"
        className="flex items-center justify-center border-red bg-destructive text-brown"
      >
        {localize('noDataMessage')}
      </Typography>
    );

  if (certTypesLoading) return <></>;

  return (
    <FreelancerSignUpPageContainer
      showHeader
      showGoBackButton
      onGoBackClick={goToCertificatesInformation}
      dialogInstructions={{
        component: <CertificatesInformationDialog />,
      }}
      videoInstructions={{
        url: videoUrl,
        videoPreviewImageURL,
      }}
      headerTitle={localize('title')}
    >
      <>
        <Typography
          as="p"
          variant="body-medium"
          className="mt-6 w-full text-gray500"
        >
          {localize('helperMessage')}
        </Typography>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmitCertificate}
        >
          {({ setFieldValue }) => (
            <Form className="w-full mb-16">
              <Typography
                as="h3"
                variant="body-large-bold"
                className="text-gray600 mt-6 mb-2"
              >
                {localize('certificateName')}
              </Typography>
              <ComboBoxField
                animated
                OptionsContainerClassName="bg-white z-30"
                name="dtype"
                options={data}
                onValueChange={(value: string) => {
                  setFieldValue('dtype', value);
                  const requireData =
                    certificatesTypes.find((cert) => cert?.id === Number(value))
                      ?.require_validation_date || false;
                  setFieldValue(
                    'isValidationDateRequired',
                    requireData.toString(),
                  );
                }}
              />
              <input
                type="text"
                name="isValidationDateRequired"
                className="h-0 w-0"
              />

              <Typography
                as="h3"
                variant="body-large-bold"
                className="text-gray600 mt-6 flex w-full justify-start items-center"
              >
                {localize('validUntil')}
              </Typography>
              <div className="relative w-full text-gray500 text-body-medium mt-2 h-10">
                <DateInputField
                  inputClassName="pl-12"
                  placeholder={`DD-MM-YYYY ${localize('ifApplicable')}`}
                  disabled={isLoading}
                  name="validUntil"
                  className="h-full w-full"
                />
              </div>
              <FileInputField
                wrapperClassName="mt-3"
                disabled={isLoading}
                name="file"
                label={localize('uploadProof')}
                accept={SUPPORTED_DOC_TYPES.join(', ')}
                onResetFile={() =>
                  setFieldValue('file', null as unknown as File)
                }
                onChange={(event) => {
                  setFieldValue('file', event.currentTarget?.files?.[0]);
                }}
              />

              <Button
                loading={isLoading}
                label={localize('saveCertificate')}
                variant="primary"
                className="flex justify-center items-center w-full h-10 gap-2 py-2 px-8 border border-gray100 mt-6"
                type="submit"
              />
            </Form>
          )}
        </Formik>
      </>
    </FreelancerSignUpPageContainer>
  );
};

export default AddCertificationDocument;
